import {
  COMPANY_KEYS,
  matchAmbassadorshipAffectedInfluencers,
  matchCompanyAmbassadorsPending,
  useCompanyPendingAmbassadorQuery,
} from "@/api/companyApi";
import {matchGroupInfluencers} from "@/api/groupApi";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import Modal from "@/components/Common/Modal";
import AmbassadorApplication from "@/components/Common/referral/AmbassadorApplication";
import AmbassadorApplicationSkeleton from "@/components/Common/referral/AmbassadorApplicationSkeleton";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {ambassadorActionModalAtom} from "@/store/jotaiAtoms";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useAtom, type ExtractAtomValue} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

const AmbassadorActionModal: FC<{
  data?: ExtractAtomValue<typeof ambassadorActionModalAtom>;
  onClose?: () => void;
  shouldRenderInline?: boolean;
}> = ({data, onClose, shouldRenderInline}) => {
  const {t} = useTranslation(["referral", "common"]);
  const queryClient = useQueryClient();
  const [ambassadorActionModalData, setAmbassadorActionModalOpen] = useAtom(ambassadorActionModalAtom);
  const ambassadorActionModal = data ?? ambassadorActionModalData;
  const isAcceptAction = ambassadorActionModal.action === "accept";
  const {pendingAmbassador} = ambassadorActionModal;
  const {data: pendingAmbassadorPage, isLoading: isAmbassadorLoading} = useCompanyPendingAmbassadorQuery({
    companyId: ambassadorActionModal.companyId!,
    influencerId: ambassadorActionModal.influencerId!,
    queryOptions: {
      enabled:
        !ambassadorActionModal.ambassador && !!ambassadorActionModal.companyId && !!ambassadorActionModal.influencerId,
    },
  });
  const ambassador = ambassadorActionModal.ambassador ?? pendingAmbassadorPage?.results[0];
  const ambassadorshipRequestId = pendingAmbassador?.ambassadorship_request_id;

  const mutationKey =
    pendingAmbassador?.source === "application" && ambassadorshipRequestId
      ? isAcceptAction
        ? COMPANY_KEYS.mCompanyAmbassadorshipRequestAcceptance(ambassadorshipRequestId)
        : COMPANY_KEYS.mCompanyAmbassadorshipRequestRejection(ambassadorshipRequestId)
      : ambassadorActionModal.companyId
        ? isAcceptAction
          ? COMPANY_KEYS.mCompanyBrandAmbassadorAcceptance(ambassadorActionModal.companyId)
          : COMPANY_KEYS.mCompanyBrandAmbassadorRejection(ambassadorActionModal.companyId)
        : [""]; // TODO: add agency mutation keys, when backend supports it

  const closeModal = onClose ?? (() => setAmbassadorActionModalOpen({ambassador: null, isOpen: false}));

  const {mutate: handleInfluencerAction, isPending} = useMutation({
    mutationKey,
    mutationFn: () =>
      client.post(
        mutationKey[0],
        pendingAmbassador?.source === "application" ? undefined : {ambassador_id: pendingAmbassador?.ambassador_id},
      ),
    onSuccess: async () => {
      capturePostHogEvent(
        isAcceptAction ? PostHogEvents.PendingAmbassadorAccepted : PostHogEvents.PendingAmbassadorDeclined,
        {
          ambassadorSource: pendingAmbassador?.source,
          companyId: ambassadorActionModal.companyId,
          entryPoint: ambassadorActionModal.entryPoint,
          influencerId: ambassadorActionModal.influencerId ?? ambassador?.influencer?.uuid,
        },
      );
      if (ambassadorActionModal.companyId) {
        [
          {queryKey: COMPANY_KEYS.qCompanyAmbassadors(ambassadorActionModal.companyId)},
          matchCompanyAmbassadorsPending(ambassadorActionModal.companyId),
          {queryKey: COMPANY_KEYS.qCompanyReferralStatistics(ambassadorActionModal.companyId)},
          ...(ambassadorActionModal.groupId ? [matchGroupInfluencers(ambassadorActionModal.groupId)] : []),
          matchAmbassadorshipAffectedInfluencers,
        ].map(filter => queryClient.invalidateQueries({...filter, refetchType: "all"}));

        if (ambassadorActionModal.influencerId) {
          await queryClient.invalidateQueries({
            queryKey: COMPANY_KEYS.qCompanyAmbassadorshipEligibility(
              ambassadorActionModal.companyId,
              ambassadorActionModal.influencerId,
            ),
            refetchType: "all",
          });
        }
      }
      // TODO: add agency query keys, when backend supports it

      closeModal();
    },
    onError: () => {
      toast.error(t("common:an_error_occurred"));
    },
  });

  return (
    <Modal
      backgroundClassName={shouldRenderInline ? undefined : "max-w-145"}
      confirmButtonClassName="flex-3"
      confirmButtonText={t(isAcceptAction ? "common:accept" : "common:decline")}
      isLoading={isPending}
      modalTitle={t(isAcceptAction ? "accept_ambassador" : "decline_ambassador")}
      onClose={closeModal}
      onConfirm={handleInfluencerAction}
      shouldCloseOnConfirm={false}
      shouldRenderInline={shouldRenderInline}>
      <span className="text-neutral-black text-2.5xl leading-8.5 font-semibold">
        {t(isAcceptAction ? "accept_ambassador_disclaimer" : "decline_ambassador_disclaimer")}
      </span>
      {isAmbassadorLoading ? (
        <AmbassadorApplicationSkeleton showActions={false} />
      ) : (
        ambassador && <AmbassadorApplication ambassador={ambassador} showActions={false} />
      )}
      <div className="flex items-center gap-2">
        {!isAcceptAction && <WarningIcon className="text-warning-dark-yellow size-3.5" />}
        <span className="text-neutral-grey-dark text-sm">
          {t(isAcceptAction ? "advantages" : "action_is_final_disclaimer")}
        </span>
      </div>
    </Modal>
  );
};

export default AmbassadorActionModal;
